export type UnitReviewCategory = 'temporary' | 'permanent'

export interface UnitReviewReason {
    value: string
    label: string
}

export interface UnitReviewOption extends UnitReviewReason {
    category: UnitReviewCategory
}

export const UNIT_REVIEW_OPTIONS: Record<UnitReviewCategory, UnitReviewOption[]> = {
    temporary: [
        { category: 'temporary', value: 'overpriced_rent', label: 'Overpriced Rent' },
        { category: 'temporary', value: 'active_nearby_construction', label: 'Active Nearby Construction' },
        { category: 'temporary', value: 'tenant_is_not_cooperative', label: 'Tenant is not cooperative' },
        { category: 'temporary', value: 'deferred_maintenance', label: 'Deferred Maintenance' },
        { category: 'temporary', value: 'poor_building_upkeep', label: 'Poor Building Upkeep' },
        { category: 'temporary', value: 'over_listing_ad_fatigue', label: 'Over-Listing / Ad Fatigue' }
    ],
    permanent: [
        { category: 'permanent', value: 'awkward_or_different_layout', label: 'Awkward or Different Layout' },
        { category: 'permanent', value: 'smaller_square_footage', label: 'Smaller Square Footage' },
        { category: 'permanent', value: 'missing_maids_room', label: "Missing Maid's Room" },
        { category: 'permanent', value: 'location_infrastructure_drawbacks', label: 'Location & Infrastructure Drawbacks' }
    ]
}

export const getUnitReviewReasonLabel = (category: UnitReviewCategory, value: string): string =>
    UNIT_REVIEW_OPTIONS[category].find((item) => item.value === value)?.label ?? value

const normalizeRemoteOptions = (value: unknown): Record<UnitReviewCategory, UnitReviewReason[]> | null => {
    let config = value
    if (typeof config === 'string') {
        try {
            config = JSON.parse(config)
        } catch {
            return null
        }
    }
    if (config == null || typeof config !== 'object' || Array.isArray(config)) return null
    const raw = config as Record<string, unknown>
    const categories: UnitReviewCategory[] = ['temporary', 'permanent']
    const normalized = {} as Record<UnitReviewCategory, UnitReviewReason[]>
    for (const category of categories) {
        const rawOptions = raw[category]
        if (!Array.isArray(rawOptions)) return null
        const options: UnitReviewReason[] = []
        for (const item of rawOptions) {
            if (item == null || typeof item !== 'object' || Array.isArray(item)) return null
            const option = item as Record<string, unknown>
            if (typeof option.value !== 'string' || typeof option.label !== 'string') return null
            const reason = option.value.trim()
            const label = option.label.trim()
            if (reason === '' || label === '') return null
            if (!options.some((existing) => existing.value === reason)) options.push({ value: reason, label })
        }
        if (options.length === 0) return null
        normalized[category] = options
    }
    return normalized
}

export const getUnitReviewSelectOptions = (category: UnitReviewCategory, remoteConfig?: unknown, selectedReasons: UnitReviewReason[] = []): UnitReviewReason[] => {
    const remote = normalizeRemoteOptions(remoteConfig)
    const configured = remote?.[category] ?? UNIT_REVIEW_OPTIONS[category].map(({ value, label }) => ({ value, label }))
    return [...configured, ...selectedReasons.filter((reason) => !configured.some((option) => option.value === reason.value))]
}

export const formatUnitReviewReasonLabels = (reasons: UnitReviewReason[]): string =>
    reasons.map(({ label }) => label).join(' \u00B7 ')
